import { useCallback, useEffect, useState } from "react";
import {
  getPushPublicKey,
  removeProfilePushSubscription,
  saveProfilePushSubscription,
} from "../lib/api.ts";
import { setPushAutoEnabled } from "../lib/storage.ts";
import type { PushStatus } from "./usePushNotifications.ts";

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function useProfilePushNotifications(profileToken: string | null) {
  const [status, setStatus] = useState<PushStatus>(() => (pushSupported() ? "loading" : "unsupported"));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profileToken || !pushSupported()) return;
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (Notification.permission !== "granted") {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    void navigator.serviceWorker.ready
      .then(async (registration) => {
        const existing = await registration.pushManager.getSubscription();
        const subscription = existing ?? await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(await getPushPublicKey()),
        });
        await saveProfilePushSubscription(profileToken, subscription.toJSON());
        if (!cancelled) setStatus("subscribed");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [profileToken]);

  const enable = useCallback(async () => {
    if (!profileToken || !pushSupported()) return;
    setStatus("loading");
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus("denied");
        setError("Уведомления не разрешены в настройках iPhone.");
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription() ??
        await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(await getPushPublicKey()),
        });
      await saveProfilePushSubscription(profileToken, subscription.toJSON());
      setPushAutoEnabled(true);
      setStatus("subscribed");
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "Не удалось включить уведомления.");
    }
  }, [profileToken]);

  const disable = useCallback(async () => {
    if (!profileToken || !pushSupported()) return;
    setStatus("loading");
    setError(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await removeProfilePushSubscription(profileToken, subscription.endpoint);
        await subscription.unsubscribe();
      }
      setPushAutoEnabled(false);
      setStatus("idle");
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "Не удалось отключить уведомления.");
    }
  }, [profileToken]);

  return { status, error, enable, disable };
}
